import {
    SomacylOrderENTITY,
    ReasonNoFinalizedSomacyl,
    StateInternalOrderSomacyl,
    FilesDocDTO,
    enumToArray,
    ClasificacionIncidencia
} from 'sigo-entities'
import * as yup from 'yup'
import { DtoValueOnSelect } from '../../shared/Domain'
import { Formik, FormikHelpers } from 'formik'
import { AdapterGenerico } from '../../shared/Infraestructure/AdapterGenerico'
import { useFinalizeSomacylOrdersMutation } from '../../shared/Infraestructure/slice'
import { Button, Col, Form, Row } from 'react-bootstrap'
import { InputNumber } from '../../shared/Components/ReactBootstrap/InputNumber/InputNumber'
import { InputSelect } from "../../shared/Components/ElementInputsCostume"
import { Dispatch, SetStateAction, useState } from 'react'
import { AdapterGeolocation } from '../../shared/Infraestructure/AdapterGeolocation'
import { EntityDataUsuario } from '../../shared/Domain/EntityDataUsuario'
import { FTPService } from '../../shared/Infraestructure/AdapterFileSystem'

interface IProps {
    row: SomacylOrderENTITY
    user: EntityDataUsuario
    setShowFinalizeOrder: Dispatch<SetStateAction<boolean>>
    getByIdSomacylOrders: () => Promise<SomacylOrderENTITY[]>
}

export interface IBodyFinalizeOrder {
    estado_interno: StateInternalOrderSomacyl
    motivo_no_finalizado: ReasonNoFinalizedSomacyl
    observaciones_en_legados: string
    latitud: number
    longitud: number
    clasificacion_incidencia: ClasificacionIncidencia
    usuario: {
        Identificacion: string,
        Nombres: string,
        ApellidoPaterno: string,
        ApellidoMaterno: string,
    }
}

const dataClasificacion = enumToArray(ClasificacionIncidencia).filter(el => el.id !== ClasificacionIncidencia.SIN_CLASIFICACION)

type FotoRequerida = { label: string, get: (row: SomacylOrderENTITY) => FilesDocDTO }

const FOTO_ANTES: FotoRequerida = { label: 'FOTO ANTES', get: (row) => row.entregables.foto_antes }

const FOTOS_FINALIZADA: FotoRequerida[] = [
    FOTO_ANTES,
    { label: 'FOTO DESPUES', get: (row) => row.entregables.foto_despues },
    { label: 'CONJUNTO DE MEDIDA ANTES', get: (row) => row.entregables.conjunto_medida_antes },
    { label: 'CONJUNTO DE MEDIDA DESPUES', get: (row) => row.entregables.conjunto_medida_despues },
]

// Si la orden se finaliza completa, se exigen las 4. Si se marca como
// NO_FINALIZADA (el trabajo no se completó), solo tiene sentido exigir la foto
// de "antes": las fotos de "después" y de conjunto de medida corresponden a un
// trabajo terminado que en este caso no ocurrió.
const getFotosRequeridas = (estado: StateInternalOrderSomacyl): FotoRequerida[] =>
    estado === StateInternalOrderSomacyl.NO_FINALIZADA ? [FOTO_ANTES] : FOTOS_FINALIZADA

export function FinalizeOrder(props: IProps) {
    const { row, user, setShowFinalizeOrder, getByIdSomacylOrders } = props

    const [finalizeSomacylOrders, { isLoading }] = useFinalizeSomacylOrdersMutation()
    const [loadingLocation, setLoadingLocation] = useState(false)
    const [verificandoFotos, setVerificandoFotos] = useState(false)
    // Mismo cálculo que AccordionAdjuntos/Photos: el nombre real con el que el
    // archivo quedó en el servidor FTP.
    const paisDelegacion = `${user.Pais.Nombre}_${user.Delegacion.Codigo}`
        .replaceAll('Ñ', 'N')
        .replaceAll('ñ', 'n')

    // El backend solo valida que la entidad en Mongo tenga los 4 Filename, no que
    // los archivos existan realmente en el servidor de ficheros. Esto confirma
    // contra el servidor antes de permitir finalizar, para no dejar una orden
    // FINALIZADA con fotos "fantasma" (Filename guardado pero sin archivo real).
    // El GPS del dispositivo puede fallar al finalizar (timeout, mala señal,
    // permiso denegado), devolviendo (0,0). Se reintenta una vez y, si sigue
    // fallando, se usan como respaldo las coordenadas ya capturadas en alguna
    // de las fotos de la orden, para no finalizar con una ubicación (0,0).
    const getCoordenadasConRespaldo = async (): Promise<{ latitud: number, longitud: number }> => {
        let coordenadas = await AdapterGeolocation.getLocation()
        if (coordenadas.latitud === 0 && coordenadas.longitud === 0) {
            coordenadas = await AdapterGeolocation.getLocation()
        }
        if (coordenadas.latitud !== 0 || coordenadas.longitud !== 0) {
            return coordenadas
        }

        const fotos = [
            row.entregables.foto_antes,
            row.entregables.conjunto_medida_antes,
            row.entregables.foto_despues,
            row.entregables.conjunto_medida_despues,
        ]
        for (const foto of fotos) {
            const latitud = Number(foto?.Coordenadas?.Y)
            const longitud = Number(foto?.Coordenadas?.X)
            if (latitud && longitud && !Number.isNaN(latitud) && !Number.isNaN(longitud)) {
                return { latitud, longitud }
            }
        }

        return coordenadas
    }

    const verifyPhotosOnServer = async (estado: StateInternalOrderSomacyl): Promise<string[]> => {
        const faltantes: string[] = []
        for (const { label, get } of getFotosRequeridas(estado)) {
            const archivo = get(row)
            if (!archivo?.Filename) {
                faltantes.push(label)
                continue
            }
            const expectedName = `${paisDelegacion}_${archivo.Ruta}-${archivo.Filename}`
            try {
                await FTPService.getFileToServer(expectedName, 0)
            } catch {
                faltantes.push(label)
            }
        }
        return faltantes
    }

    const initialValues = {
        estado_interno: StateInternalOrderSomacyl.FINALIZADA,
        motivo_no_finalizado: new DtoValueOnSelect<{
            id: ReasonNoFinalizedSomacyl;
            text: string;
        }>(),
        observaciones_en_legados: '',
        clasificacion_incidencia: new DtoValueOnSelect<{
            id: ClasificacionIncidencia;
            text: string;
        }>(),
    }

    type InitialValues = typeof initialValues

    const schema = yup.object().shape({
        estado_interno: yup
            .string()
            .min(1, 'Seleccione una opción'),
        motivo_no_finalizado: yup
            .object()
            .test({
                test(value, ctx) {
                    if (
                        this.parent.estado_interno === StateInternalOrderSomacyl.NO_FINALIZADA &&
                        value.value === ''
                    ) {
                        return ctx.createError({
                            path: `${ctx.path}.value`,
                            message: 'Seleccione un motivo',
                        })
                    }
                    return true
                }
            }),
        observaciones_en_legados: yup.string()
            .test({
                test(value = '', ctx) {
                    if (
                        this.parent.estado_interno === StateInternalOrderSomacyl.NO_FINALIZADA &&
                        value.length < 1
                    ) {
                        return ctx.createError({ message: `¡Debe ingresar una observación!` })
                    }
                    return true
                }
            }),
        clasificacion_incidencia: yup
            .object()
            .test({
                test(value, ctx) {
                    if (
                        this.parent.estado_interno === StateInternalOrderSomacyl.NO_FINALIZADA &&
                        value.value === ''
                    ) {
                        return ctx.createError({
                            path: `${ctx.path}.value`,
                            message: 'Seleccione una clasificación',
                        })
                    }
                    return true
                }
            }),
    })

    const onSubmitForm = async (
        values: InitialValues,
        formikHelpers: FormikHelpers<InitialValues>
    ) => {
        try {
            if (!navigator.onLine) {
                AdapterGenerico.createMessage('Alerta', 'Debe tener conexión a internet para finalizar la orden', 'warning')
                return
            }
            setVerificandoFotos(true)
            const faltantes = await verifyPhotosOnServer(values.estado_interno)
            setVerificandoFotos(false)
            if (faltantes.length) {
                AdapterGenerico.createMessage(
                    'Alerta',
                    `No se puede continuar: faltan las siguientes fotos en el servidor. ${faltantes.join(', ')}. Vuelva a adjuntarlas desde la pestaña de fotos.`,
                    'error'
                )
                return
            }
            setLoadingLocation(true)
            const coodenadas = await getCoordenadasConRespaldo()
            setLoadingLocation(false)
            const body: IBodyFinalizeOrder = {
                estado_interno: values.estado_interno,
                motivo_no_finalizado: values.motivo_no_finalizado.dataComplete.id,
                observaciones_en_legados: values.observaciones_en_legados,
                latitud: coodenadas.latitud,
                longitud: coodenadas.longitud,
                clasificacion_incidencia: values.estado_interno === StateInternalOrderSomacyl.FINALIZADA
                    ? ClasificacionIncidencia.SIN_CLASIFICACION
                    : values.clasificacion_incidencia.value as ClasificacionIncidencia,
                usuario: {
                    Identificacion: user.Identificacion,
                    Nombres: user.Nombres,
                    ApellidoPaterno: user.ApellidoPaterno,
                    ApellidoMaterno: user.ApellidoMaterno
                }
            }
            await finalizeSomacylOrders({ id: row._id, body }).unwrap()
            formikHelpers.resetForm()
            if (navigator.onLine) {
                await getByIdSomacylOrders()
            }
            setShowFinalizeOrder(false)
            const msg = `¡Se ${values.estado_interno === StateInternalOrderSomacyl.FINALIZADA ? 'finalizó' : 'marcó como no finalizada'} la orden!`
            AdapterGenerico.createMessage('¡Éxito!', msg, 'success')
        } catch (error) {
            console.error(error)
            AdapterGenerico.createMessage('Alerta', (error as Error).message, 'error')
        } finally {
            setVerificandoFotos(false)
            setLoadingLocation(false)
        }
    }

    return (
        <Formik
            validationSchema={schema}
            initialValues={initialValues}
            onSubmit={onSubmitForm}
        >
            {
                ({ handleSubmit, handleChange, values, touched, errors }) => {
                    return (
                        <Form
                            className='d-flex flex-column gap-2 justify-content-between'
                            noValidate
                            onSubmit={handleSubmit}
                        >
                            <div className='mb-3'>
                                <Form.Check
                                    inline
                                    label={StateInternalOrderSomacyl.FINALIZADA}
                                    name='group1'
                                    type='radio'
                                    id={`inline-radio-1`}
                                    checked={values.estado_interno === StateInternalOrderSomacyl.FINALIZADA}
                                    onChange={(e) => handleChange({ target: { value: StateInternalOrderSomacyl.FINALIZADA, name: 'estado_interno' } })}
                                />
                                <Form.Check
                                    inline
                                    label={StateInternalOrderSomacyl.NO_FINALIZADA}
                                    name='group1'
                                    type='radio'
                                    id={`inline-radio-2`}
                                    checked={values.estado_interno === StateInternalOrderSomacyl.NO_FINALIZADA}
                                    onChange={(e) => handleChange({ target: { value: StateInternalOrderSomacyl.NO_FINALIZADA, name: 'estado_interno' } })}
                                />
                            </div>
                            <div style={{ display: values.estado_interno === StateInternalOrderSomacyl.NO_FINALIZADA ? 'block' : 'none' }}>
                                <InputSelect
                                    label='Motivo'
                                    name='motivo_no_finalizado'
                                    onChange={(name, value: DtoValueOnSelect<ReasonNoFinalizedSomacyl>) => {
                                        try {
                                            handleChange({ target: { value, name } })
                                        } catch (error) {
                                            console.error(error)
                                            AdapterGenerico.createMessage('Alerta', (error as Error).message, 'error')
                                        }
                                    }}
                                    values={values}
                                    options={enumToArray(ReasonNoFinalizedSomacyl).map(el => ({
                                        label: el.id,
                                        value: el.id,
                                        dataComplete: el
                                    }))}
                                    disabled={isLoading}
                                    disabledVirtualized
                                    errors={errors}
                                    touched={touched}
                                />
                                <InputNumber
                                    type='text'
                                    label='Observaciones'
                                    name='observaciones_en_legados'
                                    errors={errors}
                                    touched={touched}
                                    values={values}
                                    handleChange={handleChange}
                                    required
                                />
                                <InputSelect
                                    label='Clasificación Incidencia'
                                    name='clasificacion_incidencia'
                                    onChange={(name, value: DtoValueOnSelect<ClasificacionIncidencia>) => {
                                        try {
                                            handleChange({ target: { value, name } })
                                        } catch (error) {
                                            console.error(error)
                                            AdapterGenerico.createMessage('Alerta', (error as Error).message, 'error')
                                        }
                                    }}
                                    values={values}
                                    options={dataClasificacion.map(el => ({
                                        label: el.id,
                                        value: el.id,
                                        dataComplete: el
                                    }))}
                                    disabled={isLoading}
                                    disabledVirtualized
                                    errors={errors}
                                    touched={touched}
                                />
                            </div>
                            <Row>
                                <Col>
                                    <Button
                                        className='w-100'
                                        disabled={isLoading || loadingLocation || verificandoFotos}
                                        type='submit'
                                        variant='secondary'
                                    >
                                        {verificandoFotos ? 'Verificando fotos…' : isLoading ? 'Cargando…' : 'Confirmar'}
                                    </Button>
                                </Col>
                            </Row>
                        </Form>
                    )
                }
            }
        </Formik>
    )
}